import { useQuery } from '@apollo/client';
import { graphql } from '../gql';
import {
  ClaimFilterInput,
  ClaimStatus,
  ClaimPaginationSort,
} from '../gql/graphql';
import { toPagination } from './utils';

export const GET_CLAIMS = graphql(`
  query getClaims(
    $pagination: PaginationRequest
    $filters: ClaimFilterInput
    $includeTasks: Boolean = true
    $sort: ClaimPaginationSort
  ) {
    claims(pagination: $pagination, filters: $filters, sort: $sort) {
      pageInfo {
        totalResults
        pageNumber
        pageSize
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          uuid
          tenant
          description
          referenceId
          status
          discoveryDate
          reportedDate
          lossDate
          dueDate
          createdAt
          reportedDate
          documentLink
          brokerEntity {
            node {
              uuid
              aliases(filters: { source: PRIMARY, type: LEGAL_NAME }) {
                edges {
                  node {
                    uuid
                    name
                  }
                }
              }
            }
          }
          insuredEntity {
            node {
              uuid
              persons {
                edges {
                  cursor
                  node {
                    uuid
                    firstName
                    lastName
                    agent
                    title
                    type
                    source
                    personType {
                      uuid
                      type
                      title
                    }
                    primary
                    contacts {
                      edges {
                        cursor
                        node {
                          uuid
                          type
                          value
                          source
                          primary
                        }
                      }
                    }
                  }
                }
              }
              aliases(filters: { source: PRIMARY, type: LEGAL_NAME }) {
                edges {
                  node {
                    uuid
                    name
                  }
                }
              }
            }
          }
          tasks @include(if: $includeTasks) {
            edges {
              node {
                uuid
                label
                type
                status
                priority
                dueDate
                note
                user {
                  cursor
                  node {
                    uuid
                    firstName
                    lastName
                  }
                }
              }
            }
          }
          claimants {
            uuid
            firstName
            lastName
            type
            companyName
            nameSearch
            contacts {
              type
              value
            }
          }
          user {
            uuid
            firstName
            lastName
          }
          quote {
            node {
              uuid
              referenceId
              policies {
                edges {
                  node {
                    uuid
                    referenceId
                    documentLink
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);
export interface IUseClaimsQueryArg {
  pageNumber?: number;
  pageSize?: number;
  filters?: ClaimFilterInput;
  includeTasks?: boolean;
  enabled?: boolean;
  sort?: ClaimPaginationSort;
}

const HAS_OPEN_CLAIMS = graphql(`
  query hasOpenClaims(
    $pagination: PaginationRequest
    $filters: ClaimFilterInput
  ) {
    claims(pagination: $pagination, filters: $filters) {
      pageInfo {
        totalResults
      }
    }
  }
`);

export interface IUseHasOpenClaimsQueryArg {
  policyUuid?: string;
  enabled?: boolean;
}

export function useHasOpenClaimsQuery({
  policyUuid,
  enabled = true,
}: IUseHasOpenClaimsQueryArg) {
  const { loading, data, error, refetch } = useQuery(HAS_OPEN_CLAIMS, {
    variables: {
      filters: {
        policyUuid,
        status: ClaimStatus.Open,
      },
    },
    fetchPolicy: 'cache-first',
    skip: !enabled || !policyUuid,
  });

  const hasOpenClaims = (data?.claims?.pageInfo?.totalResults ?? 0) > 0;

  return {
    loading,
    error,
    hasOpenClaims,
    refetch,
  };
}

export function useClaimsQuery({
  pageNumber,
  pageSize,
  filters,
  includeTasks = false,
  enabled = true,
  sort,
}: IUseClaimsQueryArg) {
  const { loading, data, error, refetch } = useQuery(GET_CLAIMS, {
    variables: {
      pagination: toPagination(pageNumber, pageSize),
      includeTasks,
      ...(filters && Object.keys(filters).length > 0 && { filters }),
      ...(sort && { sort }),
    },
    fetchPolicy: 'no-cache',
    skip: !enabled,
  });

  return {
    loading,
    error,
    data,
    refetch,
  };
}
